import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'
import { useAuth } from '@/contexts/AuthContext'
import { useAppData } from '@/contexts/AppDataContext'
import { PlusCircle } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { displayTag, tagActiveClass, computeNewTags } from '@/components/ui/TagBadges'
import { displayBeneficiaryName } from '@/utils/beneficiaryDisplay'

interface Person {
    id: string
    nom: string
    tags: string[]
}

interface EntryFormProps {
    formData: any
    onSubmit: (data: any) => void
    onNavigateHome: () => void
    showConfirm: boolean
    onConfirmNavigateHome: () => void
    onCancelConfirm: () => void
}

export const EntryForm = ({
    formData,
    onSubmit,
    onNavigateHome,
    showConfirm,
    onConfirmNavigateHome,
    onCancelConfirm
}: EntryFormProps) => {
    const { userProfile } = useAuth()
    const { summaries, workers: contextWorkers, loading } = useAppData()

    const locataires: Person[] = summaries
        .filter(s => s.is_active && (s.prenom || s.nom || s.name))
        .sort((a, b) => (a.numero_suivi ?? Infinity) - (b.numero_suivi ?? Infinity))
        .map(s => ({ id: s.id, nom: displayBeneficiaryName(s), tags: s.tags ?? [] }))

    const travailleurs: Person[] = contextWorkers
        .filter(w => w.name)
        .map(w => ({ id: w.id, nom: w.name! }))

    const [entryData, setEntryData] = useState({
        locataire: formData.entryData?.locataire || '',
        travailleur: formData.entryData?.travailleur || '',
        collegue: formData.entryData?.collegue || [],
        date: formData.entryData?.date || new Date().toISOString().split('T')[0],
        nouvelle_stat: formData.entryData?.nouvelle_stat || false,
        nouvelle_note: formData.entryData?.nouvelle_note || false
    })

    const [errors, setErrors] = useState<string[]>([])
    const [selectedBeneficiaryTags, setSelectedBeneficiaryTags] = useState<string[]>([])
    const [pendingTagsUpdate, setPendingTagsUpdate] = useState<string[] | null>(null)
    const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState<string>('')
    const [selectedWorkerId, setSelectedWorkerId] = useState<string>('')
    const [selectedColleagueIds, setSelectedColleagueIds] = useState<string[]>([])

    // Set default worker once data is loaded
    useEffect(() => {
        if (!loading && travailleurs.length > 0 && !entryData.travailleur && userProfile?.worker_id) {
            const defaultWorker = travailleurs.find(w => w.id === userProfile.worker_id)
            if (defaultWorker) {
                setEntryData(prev => ({ ...prev, travailleur: defaultWorker.nom }))
                setSelectedWorkerId(defaultWorker.id)
            }
        }
    }, [loading, travailleurs.length, userProfile?.worker_id])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // The demo lets visitors go through the flow with empty fields.
        const newErrors: string[] = []
        if (!IS_DEMO_MODE) {
            if (!entryData.locataire) newErrors.push('Nom du.de la locataire requis')
            if (!entryData.travailleur) newErrors.push('Travailleur.euse requis')
            if (!entryData.date) newErrors.push('Date requise')
            if (!entryData.nouvelle_stat && !entryData.nouvelle_note) {
                newErrors.push('Il faut cocher au moins une option (Nouvelle stat et/ou Nouvelle note)')
            }
        }

        setErrors(newErrors)

        if (newErrors.length === 0) {
            if (!IS_DEMO_MODE && pendingTagsUpdate !== null && selectedBeneficiaryId) {
                await supabase.from('beneficiaries').update({ tags: pendingTagsUpdate }).eq('id', selectedBeneficiaryId)
            }
            // With nothing ticked, the demo shows the whole path: exchange, then note.
            const wholePath = IS_DEMO_MODE && !entryData.nouvelle_stat && !entryData.nouvelle_note
            onSubmit({
                ...entryData,
                ...(wholePath ? { nouvelle_stat: true, nouvelle_note: true } : {}),
                beneficiary_id: selectedBeneficiaryId,
                worker_id: selectedWorkerId,
                colleague_ids: selectedColleagueIds,
            })
        }
    }

    const handleToggleTag = (tag: string) => {
        const currentTags = pendingTagsUpdate !== null ? pendingTagsUpdate : selectedBeneficiaryTags
        setPendingTagsUpdate(computeNewTags(currentTags, tag))
    }

    const handleStatChange = (checked: boolean) => {
        setEntryData(prev => ({ ...prev, nouvelle_stat: checked }))
    }

    const handleNoteChange = (checked: boolean) => {
        setEntryData(prev => ({ ...prev, nouvelle_note: checked }))
    }

    const toggleCollegue = (person: Person) => {
        const isSelected = entryData.collegue.includes(person.nom)
        setEntryData(prev => ({
            ...prev,
            collegue: isSelected
                ? prev.collegue.filter((n: string) => n !== person.nom)
                : [...prev.collegue, person.nom]
        }))
        setSelectedColleagueIds(prev =>
            isSelected ? prev.filter(id => id !== person.id) : [...prev, person.id]
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Nouvelle entrée" icon={<PlusCircle size={20} />} onBack={onNavigateHome} />

            <main className="max-w-4xl mx-auto py-8 px-4">
                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
                    {errors.length > 0 && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                            <h3 className="text-red-800 font-medium">Informations manquantes :</h3>
                            <ul className="list-disc list-inside text-red-700">
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nom du.de la locataire
                            </label>
                            <select
                                value={entryData.locataire}
                                onChange={(e) => {
                                    const selectedName = e.target.value
                                    setEntryData({ ...entryData, locataire: selectedName })
                                    const found = locataires.find(l => l.nom === selectedName)
                                    if (found) {
                                        setSelectedBeneficiaryId(found.id)
                                        setSelectedBeneficiaryTags(found.tags)
                                        setPendingTagsUpdate(null)
                                    } else {
                                        setSelectedBeneficiaryId('')
                                        setSelectedBeneficiaryTags([])
                                        setPendingTagsUpdate(null)
                                    }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={loading}
                            >
                                <option value="">{loading ? "Chargement..." : "Sélectionner..."}</option>
                                {locataires.map((loc: Person) => (
                                    <option key={loc.id} value={loc.nom}>{loc.nom}</option>
                                ))}
                            </select>
                            {selectedBeneficiaryId && (
                                <div className="flex items-center gap-1 mt-1">
                                    {(pendingTagsUpdate !== null ? pendingTagsUpdate : selectedBeneficiaryTags).map(tag => (
                                        <button key={tag} type="button" onClick={() => handleToggleTag(tag)}
                                            className={`px-2 py-0.5 rounded-full text-xs font-medium border ${tagActiveClass(tag)}`}>
                                            {displayTag(tag)}
                                        </button>
                                    ))}
                                    {!(pendingTagsUpdate !== null ? pendingTagsUpdate : selectedBeneficiaryTags).includes('binome') && (
                                        <button type="button" onClick={() => handleToggleTag('binome')}
                                            className="px-2 py-0.5 rounded-full text-xs border bg-white text-gray-400 border-gray-200 hover:bg-gray-50">
                                            + binôme
                                        </button>
                                    )}
                                    {!(pendingTagsUpdate !== null ? pendingTagsUpdate : selectedBeneficiaryTags).includes('solo') && (
                                        <button type="button" onClick={() => handleToggleTag('solo')}
                                            className="px-2 py-0.5 rounded-full text-xs border bg-white text-gray-400 border-gray-200 hover:bg-gray-50">
                                            + solo
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Travailleur.euse
                            </label>
                            <select
                                value={entryData.travailleur}
                                onChange={(e) => {
                                    const selectedName = e.target.value
                                    const found = travailleurs.find(t => t.nom === selectedName)
                                    setEntryData({ ...entryData, travailleur: selectedName })
                                    setSelectedWorkerId(found?.id || '')
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={loading}
                            >
                                <option value="">{loading ? "Chargement..." : "Sélectionner..."}</option>
                                {travailleurs.map((trav: Person) => (
                                    <option key={trav.id} value={trav.nom}>{trav.nom}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Collègue.s HF présent.e.s
                            </label>
                            <div
                                role="group"
                                aria-label="Collègues HF présents"
                                className="w-full max-h-44 overflow-y-auto rounded-md border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-0"
                            >
                                {loading ? (
                                    <p className="px-3 py-2 text-sm text-gray-500">Chargement...</p>
                                ) : travailleurs.length === 0 ? (
                                    <p className="px-3 py-2 text-sm text-gray-500">Aucun travailleur</p>
                                ) : (
                                    <ul className="divide-y divide-gray-100">
                                        {travailleurs.map((trav: Person) => (
                                            <li key={trav.id}>
                                                <label className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50">
                                                    <input
                                                        type="checkbox"
                                                        checked={entryData.collegue.includes(trav.nom)}
                                                        onChange={() => toggleCollegue(trav)}
                                                        disabled={loading}
                                                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-900">{trav.nom}</span>
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {entryData.collegue.length > 0 && (
                                <p className="mt-1 text-xs text-gray-500">
                                    {entryData.collegue.length} sélectionné{entryData.collegue.length > 1 ? 's' : ''}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date de la stat et/ou de la note
                            </label>
                            <input
                                type="date"
                                value={entryData.date}
                                onChange={(e) => setEntryData({ ...entryData, date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-4">Type d'entrée</label>
                            <div className="space-y-3">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={entryData.nouvelle_stat}
                                        onChange={(e) => handleStatChange(e.target.checked)}
                                        className="mr-2"
                                    />
                                    Nouvelle stat
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={entryData.nouvelle_note}
                                        onChange={(e) => handleNoteChange(e.target.checked)}
                                        className="mr-2"
                                    />
                                    Nouvelle note
                                </label>
                            </div>
                        </div>
                    </div>


                    <div className="flex justify-between">
                        <Button type="button" variant="outline" onClick={onNavigateHome}>
                            Accueil
                        </Button>
                        <Button type="submit">
                            Suivant
                        </Button>
                    </div>
                </form>
            </main>

            {showConfirm && (
                <ConfirmDialog
                    onConfirm={onConfirmNavigateHome}
                    onCancel={onCancelConfirm}
                />
            )}
        </div>
    )
} 